import React from 'react';
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    CircularProgress,
    Typography,
    Paper,
    Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import useCVList from 'features/feature_cv/hooks/useCVList';
import { useCVFiles } from '../../hooks/useCVFiles';

const SelectCVList = ({ onCVSelected, selectedId }) => {
    const { data: cvs, isLoading, error } = useCVList({ size: 10 }); // Reduced from 30 to 10
    const { data: cvFiles } = useCVFiles();

    const handleSelectCV = (cv) => {
        onCVSelected({ type: 'cv', data: cv });
    };

    const handleSelectCVFile = (cvFile) => {
        onCVSelected({ type: 'cvFile', data: cvFile });
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Paper sx={{ p: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
                <Typography variant="body2">
                    Failed to load CVs. Please try again.
                </Typography>
            </Paper>
        );
    }

    const cvList = cvs?.results || [];
    const cvFileList = cvFiles || [];
    const hasCVs = cvList.length > 0;
    const hasCVFiles = cvFileList.length > 0;

    if (!isLoading && !hasCVs && !hasCVFiles) {
        return (
            <Paper 
                sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    bgcolor: 'grey.50',
                    border: '1px dashed',
                    borderColor: 'divider'
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    No CVs found. Please create a CV first or upload a file.
                </Typography>
            </Paper>
        );
    }

    // Check if selectedId matches a CV or CV file
    const isCVSelected = (id) => {
        if (!selectedId) return false;
        const selectedCV = cvList.find(cv => cv.id === id);
        if (selectedCV) return true;
        const selectedCVFile = cvFileList.find(cvFile => cvFile.id === id);
        return !!selectedCVFile;
    };

    return (
        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
            {hasCVs && (
                <>
                    <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5, display: 'block' }}>
                        Your CVs ({cvList.length})
                    </Typography>
                    <List sx={{ p: 0, mb: hasCVFiles ? 1 : 0 }}>
                        {cvList.map((cv) => {
                            const isSelected = selectedId === cv.id;
                            return (
                                <ListItem
                                    key={cv.id}
                                    disablePadding
                                    sx={{
                                        mb: 0.5,
                                        border: '1px solid',
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        borderRadius: 1,
                                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            bgcolor: isSelected ? 'primary.50' : 'grey.50'
                                        }
                                    }}
                                >
                                    <ListItemButton
                                        onClick={() => handleSelectCV(cv)}
                                        sx={{
                                            py: 1,
                                            px: 1.5
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography 
                                                    variant="body2" 
                                                    fontWeight={isSelected ? 600 : 400}
                                                    color={isSelected ? 'primary.main' : 'text.primary'}
                                                    noWrap
                                                >
                                                    {cv.title}
                                                </Typography>
                                            }
                                        />
                                        {isSelected && (
                                            <CheckCircleIcon 
                                                sx={{ 
                                                    color: 'primary.main',
                                                    ml: 1,
                                                    fontSize: 20
                                                }} 
                                            />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </>
            )}

            {hasCVFiles && (
                <>
                    {hasCVs && <Divider sx={{ my: 1 }} />}
                    <Typography variant="caption" color="text.secondary" sx={{ px: 1, py: 0.5, display: 'block' }}>
                        Uploaded Files ({cvFileList.length})
                    </Typography>
                    <List sx={{ p: 0 }}>
                        {cvFileList.map((cvFile) => {
                            const isSelected = selectedId === cvFile.id;
                            return (
                                <ListItem
                                    key={cvFile.id}
                                    disablePadding
                                    sx={{
                                        mb: 0.5,
                                        border: '1px solid',
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        borderRadius: 1,
                                        bgcolor: isSelected ? 'primary.50' : 'background.paper',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            bgcolor: isSelected ? 'primary.50' : 'grey.50'
                                        }
                                    }}
                                >
                                    <ListItemButton
                                        onClick={() => handleSelectCVFile(cvFile)}
                                        sx={{
                                            py: 1,
                                            px: 1.5
                                        }}
                                    >
                                        <DescriptionIcon 
                                            sx={{ 
                                                fontSize: 18,
                                                color: isSelected ? 'primary.main' : 'text.secondary',
                                                mr: 1
                                            }} 
                                        />
                                        <ListItemText
                                            primary={
                                                <Typography 
                                                    variant="body2" 
                                                    fontWeight={isSelected ? 600 : 400}
                                                    color={isSelected ? 'primary.main' : 'text.primary'}
                                                    noWrap
                                                >
                                                    {cvFile.title || cvFile.name || `My CV #${cvFile.id}`}
                                                </Typography>
                                            }
                                        />
                                        {isSelected && (
                                            <CheckCircleIcon 
                                                sx={{ 
                                                    color: 'primary.main',
                                                    ml: 1,
                                                    fontSize: 20
                                                }} 
                                            />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </List>
                </>
            )}
        </Box>
    );
};

export default SelectCVList;
